<template>
  <VanPopup
    :show="show"
    @update:show="emit('update:show', $event)"
    position="bottom"
    @close="newModuleName = ''"
  >
    <div class="flex flex-col gap-2">
      <div
        class="flex justify-between items-center border-b border-b-secondary/10 p-4"
      >
        <span>{{ $t("User.mate.module.createCustom") }}</span>
        <div class="flex gap-2">
          <button
            class="btn btn-xs btn-neutral rounded btn-outline"
            @click="clickShowTemplateImport"
          >
            {{ $t("Buttons.import") }}
          </button>
          <button
            :disabled="newModuleName === ''"
            class="btn btn-xs btn-primary rounded"
            @click="confirmCreateModule"
          >
            {{ $t("Buttons.sure") }}
          </button>
        </div>
      </div>
      <div class="p-4 w-full">
        <input
          v-model="newModuleName"
          :placeholder="$t('User.mate.module.name')"
          class="input input-sm w-full rounded input-primary"
        />
      </div>
    </div>
  </VanPopup>

  <!-- 导入模板 -->
  <VanPopup
    v-model:show="showImport"
    position="bottom"
    class="h-[60%] overflow-hidden"
    closeable
  >
    <div v-if="loadingTemplate" class="flex justify-center items-center mt-32">
      <div class="loading loading-ring loading-lg"></div>
    </div>
    <div v-else-if="moduleStore.mateModuleTemplates.length === 0">
      <VanEmpty
        class="mt-12 text-center"
        :description="$t('Message.noTemplateAvailable')"
      />
    </div>
    <div v-else class="h-full overflow-hidden flex flex-col">
      <div class="text-center my-4 text-base">
        {{ $t("Message.clickToImport") }}
      </div>
      <div class="flex-1 overflow-auto">
        <VanCellGroup inset>
          <VanCell
            v-for="template in moduleStore.mateModuleTemplates"
            :key="template.id"
          >
            <template #title>
              <div class="flex justify-between">
                <div class="flex-1">
                  <span class="mr-2">{{ template.module.name }}</span>
                  <span>({{ template.name }})</span>
                </div>
                <div class="flex gap-2">
                  <button
                    class="btn btn-xs btn-primary"
                    @click.stop="clickImportTemplate(template)"
                  >
                    <i class="iconify mingcute--file-import-line"></i>
                  </button>
                  <button
                    class="btn btn-xs btn-outline"
                    @click.stop="clickEditTemplate(template)"
                  >
                    <i class="iconify mingcute--edit-3-line"></i>
                  </button>
                  <button
                    class="btn btn-xs btn-outline"
                    @click.stop="clickDeleteTemplate(template)"
                  >
                    <i class="iconify mingcute--delete-2-line"></i>
                  </button>
                </div>
              </div>
            </template>
            <template #label>
              <van-text-ellipsis
                :content="template.questions.map((v) => `${v.text}`).join(',')"
                :expand-text="$t('Buttons.expand')"
                :collapse-text="$t('Buttons.collapse')"
                @click.stop
              />
            </template>
          </VanCell>
        </VanCellGroup>
      </div>
    </div>
  </VanPopup>
</template>

<script setup lang="ts">
import {
  showLoadingToast,
  showFailToast,
  showConfirmDialog,
  showSuccessToast,
} from "vant";
import { ref } from "vue";
import { useModule } from "@/stores/module";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { MateModuleTemplate } from "@/types/entity";
import { useGlobalModal } from "@/stores/globalModal";

defineProps<{
  show: boolean;
}>();

const emit = defineEmits<{
  (e: "update:show", show: boolean): void;
  (e: "reloadModule"): void;
}>();

const { t } = useI18n();
const newModuleName = ref("");
const moduleStore = useModule();
const router = useRouter();

// 创建自定义模块
const confirmCreateModule = async () => {
  const toast = showLoadingToast({
    message: t("Message.creatingModule"),
    duration: 0,
  });
  try {
    const id = await moduleStore.createMateModule(newModuleName.value);
    emit("update:show", false);
    emit("reloadModule");
    router.replace({ hash: `#${id}` });
  } catch (e) {
    showFailToast(t("Message.failCreateModule"));
    throw e;
  } finally {
    toast.close();
  }
};

const showImport = ref(false);
const loadingTemplate = ref(false);
// 获取导入的模块
const clickShowTemplateImport = () => {
  emit("update:show", false);
  showImport.value = true;
  loadingTemplate.value = true;
  moduleStore.getTemplateList().finally(() => {
    loadingTemplate.value = false;
  });
};

const clickImportTemplate = (template: MateModuleTemplate) => {
  showConfirmDialog({
    title: t("Message.importTemplateInfo"),
    confirmButtonText: t("Buttons.sure"),
    cancelButtonText: t("Buttons.cancel"),
  })
    .then(async () => {
      showImport.value = false;
      const loading = showLoadingToast({
        message: t("Message.importingTemplate"),
        duration: 0,
      });
      try {
        await moduleStore.importTemplate(template.id);
        router.replace({ hash: `#1` });
        emit("reloadModule");
        loading.close();
        showSuccessToast(t("Message.importTemplateSuccess"));
      } catch (e) {
        loading.close();
        showFailToast(t("Message.failImportTemplate"));
      }
    })
    .catch(() => {});
};

const clickDeleteTemplate = (template: MateModuleTemplate) => {
  showConfirmDialog({
    title: t("Message.deleteConfirm"),
    confirmButtonText: t("Buttons.sure"),
    cancelButtonText: t("Buttons.cancel"),
  })
    .then(async () => {
      const loading = showLoadingToast({
        message: t("Message.deletingTemplate"),
        duration: 0,
      });
      try {
        await moduleStore.deleteTemplate(template.id);
        loading.close();
        showSuccessToast(t("Message.deleteTemplateSuccess"));
      } catch (e) {
        loading.close();
        showFailToast(t("Message.failDeleteTemplate"));
      }
    })
    .catch(() => {});
};

const clickEditTemplate = (template: MateModuleTemplate) => {
  useGlobalModal().openTextInputModal({
    title: t("Message.createTemplateTitle"),
    callback: async (name?: string) => {
      await moduleStore.updateTemplate(template.id, name || "");
    },
  });
};
</script>
